
import { useState, useCallback } from "react";
import { BirdieRecord, TeeBox, User } from "@/types";
import { useBirdieRecords } from "./useBirdieRecords";
import { useBirdieActions } from "./useBirdieActions";

interface UseBirdieModalProps {
  courseId: string;
  userId: string;
  selectedHole: number;
  mode?: 'user' | 'game';
}

export function useBirdieModal({
  courseId,
  userId,
  selectedHole,
  mode = 'user',
}: UseBirdieModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<BirdieRecord | null>(null);
  const [loading, setIsLoading] = useState(false);

  // Fetch birdie records for this hole (skip in game mode — game uses useGameBirdieRecords)
  const { birdieRecords, fetchBirdieRecords, refreshRecords } = useBirdieRecords(
    userId,
    courseId,
    selectedHole,
    mode === 'game'
  );

  const { handleSubmit: actionSubmit, handleEdit: actionEdit, handleDelete, handleCancel } = useBirdieActions({
    courseId,
    userId,
    fetchBirdieRecords,
    setIsLoading,
    resetForm: () => {
      setEditingRecord(null);
      setEditingRecordId(null);
      setIsEditing(false);
    },
    setEditingRecordId,
    setIsEditing
  });

  // Handle form submission
  const handleSubmit = useCallback(async (
    holeNumber: number,
    teeBox: TeeBox,
    date: string,
    notes: string,
    relevantGameIds: string[],
    selectedUserId: string,
    gamePlayers: Record<string, User[]>,
    presentPlayers: User[],
    scoreType: BirdieRecord['scoreType']
  ) => {
    return actionSubmit(
      holeNumber,
      teeBox,
      date,
      notes,
      relevantGameIds,
      isEditing,
      editingRecordId,
      selectedUserId,
      gamePlayers,
      presentPlayers,
      scoreType
    );
  }, [actionSubmit, isEditing, editingRecordId]);

  // User clicks edit on a history item
  const handleEdit = useCallback((record: BirdieRecord) => {
    setEditingRecord(record);
    return actionEdit(record);
  }, [actionEdit]);

  const resetForm = useCallback(() => {
    setEditingRecord(null);
    setEditingRecordId(null);
    setIsEditing(false);
  }, []);

  return {
    loading,
    isEditing,
    editingRecordId,
    editingRecord,
    setEditingRecordId,
    handleSubmit,
    resetForm,
    handleCancel,
    handleDelete,
    handleEdit,
    birdieRecords,
    refreshRecords,
  };
}
